import React, { useCallback, useContext, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { LinkButton, LinkIconButton } from '@atlaskit/button/new';
import { Inline } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';

import { firedAt } from '../../analytics';
import { useFireCrossAppProfileAnalytic } from '../../hooks/useFireCrossAppProfileAnalytic';
import { useUserProfileLink } from '../../hooks/useUserProfileLink';
import { useCardOpenTimer } from '../../store/cardOpenTimer';
import { UserProfileCardContext } from '../../store/context';
import type { PersonProfileData } from '../../types';
import { getIconForExternalMessageIntegration } from '../utils/externalMessageIntegrationIcons';
import { resolveExternalMessageFromIntegrations } from '../utils/thirdPartyIntegrationsByProduct';

const messages = defineMessages({
	viewProfile: {
		id: 'user-profile-card.action-bar.view-profile',
		defaultMessage: 'View profile',
		description: 'Button label to navigate to a user profile from the profile card',
	},
	viewOrgChartLabel: {
		id: 'user-profile-card.action-bar.view-org-chart-label',
		defaultMessage: 'View org chart for {name}',
		description: 'Accessible label for the org chart icon button on the profile card',
	},
	sendMessageLabel: {
		id: 'user-profile-card.action-bar.send-message-label',
		defaultMessage: 'Send message to {name}',
		description:
			'Accessible label for the send message button (e.g. Slack or Microsoft Teams) on the profile card',
	},
});

interface HeaderActionsProps {
	profileData: PersonProfileData;
	cloudId: string;
}

/**
 * Primary actions for the profile card header (org entry + view profile in Teams).
 */
export function HeaderActions({
	profileData,
	cloudId,
}: HeaderActionsProps): React.JSX.Element | null {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const { getDurationSinceInteraction } = useCardOpenTimer();
	const { townsquareApp, onProfileClick, getTestId } = useContext(UserProfileCardContext);
	const { accountId, name, thirdPartyIntegrations, email } = profileData;
	const { href, target, onNavigate, isCrossAppMauGateEnabled } = useUserProfileLink(
		accountId,
		cloudId,
		townsquareApp,
	);
	const fireCrossAppProfileAnalytic = useFireCrossAppProfileAnalytic(townsquareApp);

	const externalMessage = useMemo(
		() => resolveExternalMessageFromIntegrations(thirdPartyIntegrations, email),
		[email, thirdPartyIntegrations],
	);

	const handleThirdPartyMessageClick = useCallback(() => {
		// Reuse the existing `@atlaskit/profilecard` clicked.action event so the SLO dashboards
		// see a single click-event surface for both profile-card packages. `index` and `hasOnClick`
		// are required by that schema but are not meaningful for user-profile-card actions, so we
		// always send `index: 0` and `hasOnClick: true`.
		fireEvent('ui.profilecard.clicked.action', {
			firedAt: firedAt(),
			duration: getDurationSinceInteraction() ?? 0,
			hasHref: Boolean(externalMessage?.dm.openDmUrl),
			hasOnClick: true,
			index: 0,
			actionId: 'thirdPartyMessage',
		});
	}, [fireEvent, getDurationSinceInteraction, externalMessage]);

	const handleViewProfileClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
			fireEvent('ui.profilecard.clicked.action', {
				firedAt: firedAt(),
				duration: getDurationSinceInteraction() ?? 0,
				hasHref: Boolean(href),
				hasOnClick: true,
				index: 0,
				actionId: 'viewProfile',
			});
			if (isCrossAppMauGateEnabled) {
				fireCrossAppProfileAnalytic();
			}
			onProfileClick?.(accountId);
			onNavigate?.(event);
		},
		[
			fireEvent,
			getDurationSinceInteraction,
			href,
			onNavigate,
			onProfileClick,
			isCrossAppMauGateEnabled,
			fireCrossAppProfileAnalytic,
			accountId,
		],
	);

	if (!accountId || !href) {
		return null;
	}

	return (
		<Inline space="space.100" alignBlock="center">
			{externalMessage && (
				<LinkIconButton
					href={externalMessage.dm.openDmUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleThirdPartyMessageClick}
					icon={getIconForExternalMessageIntegration(externalMessage.dm.integration)}
					label={intl.formatMessage(messages.sendMessageLabel, { name })}
				/>
			)}
			{accountId && href && (
				<LinkButton
					href={href}
					appearance="default"
					target={target}
					onClick={handleViewProfileClick}
					rel={target === '_blank' ? 'noopener noreferrer' : undefined}
					testId={getTestId('view-profile-button')}
				>
					{intl.formatMessage(messages.viewProfile)}
				</LinkButton>
			)}
		</Inline>
	);
}
